import { useState, useEffect } from 'react';
import { ArrowLeftIcon, CheckIcon, CaretDownIcon, XIcon, InfoIcon } from '@phosphor-icons/react';
import { QuestionItem } from '../../shared/question-item';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
} from '../../../components/ui/pagination';
import { PieChart, Pie, Cell } from 'recharts';
import { useSelector, useDispatch } from 'react-redux';
import { toggleSimplifyMode } from '../../../../../../apps/frontend/src/redux/slices/globalSlice';
import { getChapterWiseNotebookDetails } from '../../../services/notebooks';
import NotebookDetailLoader from '../../../components/loaders/NotebookLoader';
import { SimplifyModeToggle } from '../../../components/simplifymode/SimplifyMode';
import NotebookQuestionDrawer from '../../../components/drawers/NotebookQuestionDrawer';
import { useRef } from 'react';
import { Tooltip } from 'recharts';

export const ChapterDetail = ({ notebook, onBack }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState('All Qs');
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const user = JSON.parse(localStorage.getItem('userProfile'));
  const [autoStartTimer, setAutoStartTimer] = useState(user?.settings?.toggleQuestionTimer);
  const [stats, setStats] = useState({
    correct: 0,
    wrong: 0,
    unattempted: 0,
  });
  const [initialStats, setInitialStats] = useState({
    totalCorrect: 0,
    totalIncorrect: 0,
    totalUnattempted: 0,
    totalCount: 0,
  });
  const [totalPages, setTotalPages] = useState(1);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [error, setError] = useState(null);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null);
  const [isQuestionDrawerOpen, setIsQuestionDrawerOpen] = useState(false);

  const dispatch = useDispatch();
  const simplifyMode = useSelector((state) => state.global.simplifyMode);

  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchNotebookData = async () => {
    try {
      setLoading(true);
      setError(null);
      const offset = (currentPage - 1) * ITEMS_PER_PAGE;

      let filterParams = {};
      const activeFilterLabel = activeFilter.split(' (')[0];

      if (activeFilterLabel === 'Attempted correct') {
        filterParams.correct = true;
      } else if (activeFilterLabel === 'Attempted wrong') {
        filterParams.incorrect = true;
      } else if (activeFilterLabel === 'Not attempted') {
        filterParams.unattempted = true;
      }

      const params = {
        offset,
        limit: ITEMS_PER_PAGE,
        ...filterParams,
        ...(selectedSubject && selectedSubject !== 'all' && { subject: selectedSubject }),
      };

      if (!notebook?.chapterId) {
        throw new Error('Chapter ID is missing in notebook data');
      }

      const response = await getChapterWiseNotebookDetails(notebook.chapterId, notebook.questions || [], params);

      // Handle the API response structure
      const data = response?.data || response;
      const rootData = response || {};
      // Get questions from the correct path (data.answers)
      const questionsData = data?.answers || data?.questions || [];

      // Transform questions based on the API response structure
      const transformedQuestions = Array.isArray(questionsData)
        ? questionsData.map((item, index) => {
            const question = item.question || item;
            const subject = item.subject || {};

            const testObj = Array.isArray(item.test) ? item.test[0] : item.test || {};
            const testTitle = testObj.title || 'Unknown Test';
            const testId = testObj._id || '';

            return {
              id: `Q${index + 1}`, // Custom ID like q1, q2, ...
              question: question.question?.text || question.text || '',
              questionid: question._id || 'Unknown ID',
              subject: subject.title || 'Unknown',
              subjectColor: subject.color || '#0086FF',
              test: testTitle,
              testId,
              level: question.level || 1,
              type: item.type || 'singleCorrect',
              status: item.status || 'notAnswered',
              attemptOverview: item.attemptOverview,
              isCorrect: item.isCorrect || item.optionOrSoln?.isCorrect || false,
              isAttempted: item.status !== 'notAnswered' && item.status !== 'notVisited',
              timeTaken: item.timeTaken || 0,
              notes: item.notes || '',
              userQuestionTags: item.user_question_tags || null,
              options: question.options || [],
              solution: question.solution || null,
            };
          })
        : [];

      setQuestions(transformedQuestions);
      // Extract unique subjects from questions
      const uniqueSubjects = transformedQuestions.reduce((acc, q) => {
        if (!acc.find((s) => s.title === q.subject)) {
          acc.push({
            _id: q.subject.toLowerCase().replace(/\s+/g, '-'),
            title: q.subject,
          });
        }
        return acc;
      }, []);

      setSubjects(uniqueSubjects);

      // Calculate stats from the transformed questions
      const correctCount = transformedQuestions.filter((q) => q.isCorrect).length;
      const wrongCount = transformedQuestions.filter((q) => q.isAttempted && !q.isCorrect).length;
      const unattemptedCount = transformedQuestions.filter((q) => !q.isAttempted).length;
      const totalCount = transformedQuestions.length;

      setStats({
        correct: totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0,
        wrong: totalCount > 0 ? Math.round((wrongCount / totalCount) * 100) : 0,
        unattempted: totalCount > 0 ? Math.round((unattemptedCount / totalCount) * 100) : 0,
      });

      // Use totalPages from API response instead of calculating locally
      setTotalPages(rootData?.totalPages || 1);

      // Store fixed stats for all filters & pie chart
      if (currentPage === 1 && activeFilter === 'All Qs') {
        setInitialStats({
          totalCorrect: data?.totalCorrect || correctCount,
          totalIncorrect: data?.totalIncorrect || wrongCount,
          totalUnattempted: data?.totalUnattempted || unattemptedCount,
          totalCount: data?.totalCorrect + data?.totalIncorrect + data?.totalUnattempted || totalCount,
        });
      }
    } catch (error) {
      setError('Failed to load notebook details. Please try again.');
      console.error('Error fetching notebook details:', error);
      setQuestions([]);
      setSubjects([]);
      setStats({
        correct: 0,
        wrong: 0,
        unattempted: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (notebook?.id || notebook?.chapterId) {
      fetchNotebookData();
    }
  }, [notebook?.id, notebook?.chapterId, currentPage, selectedSubject, activeFilter]);

  const handleFilterChange = (filterLabel) => {
    const label = filterLabel.split(' (')[0];
    setActiveFilter(label);
    setCurrentPage(1);
  };

  const handleSubjectFilter = (subjectId) => {
    setSelectedSubject(subjectId);
    setCurrentPage(1);
  };

  const handleQuestionClick = (index) => {
    if (ignoreNextClickRef.current) {
      ignoreNextClickRef.current = false;
      return;
    }
    setSelectedQuestionIndex(index);
    setIsQuestionDrawerOpen(true);
  };
  const ignoreNextClickRef = useRef(false);

  const handleQuestionChange = (newIndex) => {
    setSelectedQuestionIndex(newIndex);
  };

  const filters = [
    { id: 'all-qs', label: `All Qs (${initialStats.totalCount})` },
    { id: 'attempted-correct', label: `Attempted correct (${initialStats.totalCorrect})` },
    { id: 'attempted-wrong', label: `Attempted wrong (${initialStats.totalIncorrect})` },
    { id: 'not-attempted', label: `Not attempted (${initialStats.totalUnattempted})` },
  ];

  const total = initialStats.totalCount || 1; // prevent divide by 0

  const pieData = [
    {
      name: 'Attempted wrong',
      value: Math.round((initialStats.totalIncorrect / total) * 100),
    },
    {
      name: 'Attempted correct',
      value: Math.round((initialStats.totalCorrect / total) * 100),
    },
    {
      name: 'Not Attempted',
      value: Math.round((initialStats.totalUnattempted / total) * 100),
    },
  ];

  const COLORS = ['var(--q3-danger-normal)', 'var(--q3-success-normal)', 'var(--q3-neutral-light)'];

  // keeping Custom tooltip formatter (optional)
  const renderCustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 rounded px-2 py-1 bg-[var(--q3-surface-default)] shadow text-xs font-medium border border-[var(--q3-stroke-normal)] whitespace-nowrap z-10">
          {`${payload[0].name}: ${payload[0].value.toFixed(2)}%`}
        </div>
      );
    }
    return null;
  };

  const handleToggleSimplifyMode = () => {
    dispatch(toggleSimplifyMode());
  };

  if (loading) {
    return <NotebookDetailLoader />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-6 bg-[var(--q3-surface-default)] rounded-lg">
          <p className="text-[var(--q3-neutral-default)] mb-4">{error}</p>
          <button onClick={fetchNotebookData} className="px-4 py-2 bg-[var(--q3-accent-normal)] text-white rounded-lg">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="min-h-screen bg-[var(--q3-surface-default)] pb-20">
        {' '}
        {/* Increased padding-bottom for mobile */}
        {/* Mobile Header */}
        <div className="sticky top-0 bg-[var(--q3-surface-default)] border-b border-[var(--q3-stroke-normal)] px-4 py-3 z-10">
          <div className="flex items-center gap-3 mb-2">
            <button onClick={onBack} className="p-2 -ml-2">
              <ArrowLeftIcon size={20} className="text-[var(--q3-neutral-default)]" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-[var(--q3-neutral-default)] truncate">{notebook.title}</h1>
            </div>
          </div>

          {/* Mobile Filters - Updated to match desktop styling */}
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
            {filters.map((filter) => {
              const filterLabel = filter.label.split(' (')[0];
              return (
                <button
                  key={filter.id}
                  onClick={() => handleFilterChange(filter.label)}
                  className={`relative flex items-center gap-1 px-3 py-1.5 rounded-full text-xs transition-colors whitespace-nowrap flex-shrink-0
                ${
                  activeFilter === filterLabel
                    ? 'bg-[var(--q3-accent-normal)] text-white '
                    : 'text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] hover:bg-[var(--q3-surface-dimmest)] border border-[var(--q3-stroke-normal)]'
                }`}
                >
                  {filter.label}
                  {activeFilter === filterLabel && <CheckIcon size={14} weight="bold" className="ml-1" />}
                </button>
              );
            })}
          </div>
        </div>
        <div className="px-4">
          {/* Mobile Stats Card - Updated to match desktop layout */}
          {questions.length > 0 && (
            <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-lg p-4 mb-4 mt-4">
              <div className="flex flex-col gap-4">
                <div>
                  <h3 className="text-base font-semibold text-[var(--q3-neutral-default)] mb-1">Qs in this notebook</h3>
                  <p className="text-xs text-[var(--q3-neutral-light)]">
                    This pie chart shows your attempt status of questions added in this notebook
                  </p>
                </div>

                <div className="flex items-center gap-4 -mt-4">
                  <div className="w-20 h-20 flex-shrink-0">
                    <PieChart width={80} height={80} className="mt-10">
                      <Pie data={pieData} innerRadius={24} outerRadius={40} dataKey="value" stroke="none">
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index]} />
                        ))}
                      </Pie>
                      <Tooltip content={renderCustomTooltip} />
                    </PieChart>
                  </div>

                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--q3-stroke-normal)] rounded bg-[var(--q3-surface-default)]">
                      <XIcon size={16} weight="bold" className="text-red-400" />
                      <span className="text-xs text-[var(--q3-neutral-light)] whitespace-nowrap">
                        Attempted wrong{' '}
                        <span className="font-semibold text-[var(--q3-neutral-default)]">
                          ({Math.round((stats.wrong / (stats.correct + stats.wrong + stats.unattempted || 1)) * 100)}%)
                        </span>
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--q3-stroke-normal)] rounded bg-[var(--q3-surface-default)]">
                      <CheckIcon size={16} weight="bold" className="text-green-400" />
                      <span className="text-xs text-[var(--q3-neutral-light)] whitespace-nowrap">
                        Attempted correct{' '}
                        <span className="font-semibold text-[var(--q3-neutral-default)]">
                          ({Math.round((stats.correct / (stats.correct + stats.wrong + stats.unattempted || 1)) * 100)}
                          %)
                        </span>
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--q3-stroke-normal)] rounded bg-[var(--q3-surface-default)]">
                      <InfoIcon size={16} weight="bold" className="text-gray-400" />
                      <span className="text-xs text-[var(--q3-neutral-light)] whitespace-nowrap">
                        Not Attempted{' '}
                        <span className="font-semibold text-[var(--q3-neutral-default)]">
                          (
                          {Math.round(
                            (stats.unattempted / (stats.correct + stats.wrong + stats.unattempted || 1)) * 100,
                          )}
                          %)
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mobile Questions List - Updated to match desktop styling */}
          <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-lg mb-4">
            <h3 className="text-base font-semibold text-[var(--q3-neutral-default)] p-3 border-b border-[var(--q3-stroke-normal)]">
              All Bookmarked Qs
            </h3>
            <div className="divide-y divide-[var(--q3-stroke-normal)]">
              {questions.length > 0 ? (
                questions.map((question, index) => (
                  <div
                    key={question.id}
                    className="p-3 hover:bg-[var(--q3-surface-dim)] cursor-pointer"
                    onClick={(e) => {
                      if (!e.target.closest('.bookmark-button')) {
                        handleQuestionClick(index);
                      }
                    }}
                  >
                    <QuestionItem
                      id={question.id}
                      question={question.question}
                      questionid={question.questionid}
                      status={question.status}
                      type={question.type}
                      testInfo={question.test}
                      note={question.notes}
                      isBookmarked={true}
                      onBookmarkOpen={() => (ignoreNextClickRef.current = true)}
                      packId={notebook.packId}
                      testId={question.testId}
                      notebookData={notebook}
                    />
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-[var(--q3-neutral-light)]">
                    {activeFilter === 'All Qs' && questions.length === 0
                      ? 'No questions found'
                      : `No ${activeFilter.toLowerCase()} questions found`}
                  </p>
                  {activeFilter !== 'All Qs' && (
                    <button
                      onClick={() => setActiveFilter('All Qs')}
                      className="mt-2 px-4 py-2 text-sm bg-[var(--q3-accent-normal)] text-white rounded-lg"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Pagination - Updated to match desktop styling */}
          {questions.length > 0 && (
            <div className="mt-6">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="h-9 w-9 p-0"
                    />
                  </PaginationItem>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <PaginationItem key={page}>
                      <PaginationLink
                        isActive={page === currentPage}
                        onClick={() => setCurrentPage(page)}
                        className="h-9 w-9 p-0"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  <PaginationItem>
                    <PaginationNext
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="h-9 w-9 p-0"
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>
        {/* Simplify Mode Toggle - Fixed at bottom like desktop */}
        <div className="fixed bottom-4 left-0 right-0 flex justify-center px-4">
          <div
            className="inline-flex min-h-[48px] px-4 py-2 items-center gap-3 rounded-[12px] cursor-pointer transition-all duration-300 border bg-[var(--q3-surface-glass)] backdrop-blur-sm"
            style={{
              borderImage: 'linear-gradient(to right, #FF6B00, var(--q3-stroke-light)) 1',
              borderImageSlice: 1,
              borderWidth: '1px',
              borderStyle: 'solid',
              borderRadius: '12px',
              boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
            }}
            onClick={() => dispatch(toggleSimplifyMode())}
          >
            <span className="text-sm font-medium text-[var(--q3-neutral-default)]">
              Simplify Mode is {simplifyMode ? 'On' : 'Off'}
            </span>
            <div
              className={`w-12 h-6 rounded-full relative transition-colors duration-300 ${
                simplifyMode ? 'bg-[var(--q3-accent-normal)]' : 'bg-[var(--q3-stroke-normal)]'
              }`}
            >
              <div
                className={`w-5 h-5 bg-white rounded-full absolute top-0.5 shadow-md transition-transform duration-300 ${
                  simplifyMode ? 'translate-x-[26px]' : 'translate-x-0.5'
                }`}
              />
            </div>
          </div>
        </div>
        {/* Question Drawer - Using same component as desktop */}
        {isQuestionDrawerOpen && selectedQuestionIndex !== null && (
          <NotebookQuestionDrawer
            isOpen={isQuestionDrawerOpen}
            onClose={() => setIsQuestionDrawerOpen(false)}
            questions={questions}
            currentQuestionIndex={selectedQuestionIndex}
            onQuestionChange={handleQuestionChange}
            testId={notebook.testId}
            packId={notebook.packId}
            autoStartTimer={autoStartTimer}
            onAutoStartTimerChange={setAutoStartTimer}
          />
        )}
      </div>
    );
  }

  // Desktop version
  return (
    <div className="flex-1 p-6">
      {/* Header - unchanged */}
      <div className="flex items-start justify-between mb-6">
        {/* Left: Arrow + Title + Breadcrumb */}
        <div className="flex flex-col gap-3">
          <button onClick={onBack} className="w-fit p-2 rounded-full bg-[var(--q3-surface-dimmer)]">
            <ArrowLeftIcon size={20} />
          </button>
          <h1
            className="text-[var(--q3-neutral-default)] font-bold"
            style={{
              fontFamily: 'Inter',
              fontSize: 'var(--font-display-2xl-font_size, 24px)',
              fontWeight: 'var(--font-display-2xl-font_weight, 700)',
              lineHeight: 'var(--font-display-2xl-line_height, 32px)',
            }}
          >
            {notebook.title}
          </h1>
        </div>
      </div>

      {/* Filters-laptop - unchanged */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto whitespace-nowrap">
        {filters.map((filter) => {
          const filterLabel = filter.label.split(' (')[0];
          return (
            <button
              key={filter.id}
              onClick={() => handleFilterChange(filter.label)}
              className={`relative flex items-center gap-1 px-4 py-2 pr-4 rounded-full text-sm transition-colors whitespace-nowrap
          ${
            activeFilter === filterLabel
              ? 'bg-[var(--q3-accent-normal)] text-white '
              : 'text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] hover:bg-[var(--q3-surface-dimmest)] border border-[var(--q3-stroke-normal)]'
          }`}
            >
              {filter.label}
              {activeFilter === filterLabel && <CheckIcon size={14} weight="bold" className="ml-1" />}
            </button>
          );
        })}
      </div>

      {/* Stats Card - updated border styling */}
      {questions.length > 0 && (
        <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-lg px-6 py-0 mb-4">
          <div className="flex items-center justify-between gap-6">
            {/* Left Text Section */}
            <div className="min-w-[200px]">
              <h3 className="text-lg font-semibold text-[var(--q3-neutral-default)] mb-1">Qs in this notebook</h3>
              <p className="text-xs text-[var(--q3-neutral-light)] m-0 whitespace-normal">
                This pie chart shows your attempt status of questions
                <br />
                added in this notebook
              </p>
            </div>

            {/* Pie Chart */}
            <div className="relative w-20 h-20 flex items-center justify-center ml-auto select-none touch-none">
              {pieData.length > 0 && (
                <PieChart
                  width={80}
                  height={80}
                  className="select-none touch-none"
                  style={{ userSelect: 'none', WebkitUserSelect: 'none', outline: 'none' }}
                >
                  <Pie
                    data={pieData}
                    innerRadius={24}
                    outerRadius={40}
                    dataKey="value"
                    stroke="none"
                    className="select-none touch-none"
                    style={{ userSelect: 'none', WebkitUserSelect: 'none', outline: 'none' }}
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                        className="select-none touch-none"
                        style={{ userSelect: 'none', WebkitUserSelect: 'none', outline: 'none' }}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={renderCustomTooltip} />
                </PieChart>
              )}
            </div>

            {/* Status Boxes */}
            <div className="flex flex-row gap-3 text-sm">
              <div className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--q3-stroke-normal)] rounded bg-[var(--q3-surface-default)]">
                <XIcon size={16} weight="bold" className="text-red-400" />
                <span className="text-xs text-[var(--q3-neutral-light)] whitespace-nowrap">
                  Attempted wrong{' '}
                  <span className="font-semibold text-[var(--q3-neutral-default)]">
                    ({Math.round((stats.wrong / (stats.correct + stats.wrong + stats.unattempted || 1)) * 100)}%)
                  </span>
                </span>
              </div>

              <div className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--q3-stroke-normal)] rounded bg-[var(--q3-surface-default)]">
                <CheckIcon size={16} weight="bold" className="text-green-400" />
                <span className="text-xs text-[var(--q3-neutral-light)] whitespace-nowrap">
                  Attempted correct{' '}
                  <span className="font-semibold text-[var(--q3-neutral-default)]">
                    ({Math.round((stats.correct / (stats.correct + stats.wrong + stats.unattempted || 1)) * 100)}%)
                  </span>
                </span>
              </div>

              <div className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--q3-stroke-normal)] rounded bg-[var(--q3-surface-default)]">
                <InfoIcon size={16} weight="bold" className="text-gray-400" />
                <span className="text-xs text-[var(--q3-neutral-light)] whitespace-nowrap">
                  Not Attempted{' '}
                  <span className="font-semibold text-[var(--q3-neutral-default)]">
                    ({Math.round((stats.unattempted / (stats.correct + stats.wrong + stats.unattempted || 1)) * 100)}%)
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-lg mb-6 ">
        <h3 className="text-lg font-semibold text-[var(--q3-neutral-default)]  p-4 border-b border-[var(--q3-stroke-normal)]">
          All Bookmarked Qs
        </h3>
        <div className="space-y-0">
          {questions.length > 0 ? (
            questions.map((question, index) => (
              <div
                key={question.id}
                className="pb-4 last:border-b-0 last:pb-0 p-2 border-b border-[var(--q3-stroke-normal)] hover:bg-[var(--q3-surface-dim)] cursor-pointer"
                onClick={(e) => {
                  // Only trigger if the click wasn't on the bookmark button
                  if (!e.target.closest('.bookmark-button')) {
                    handleQuestionClick(index);
                  }
                }}
              >
                <QuestionItem
                  id={question.id}
                  question={question.question}
                  questionid={question.questionid}
                  status={question.status}
                  type={question.type}
                  testInfo={question.test}
                  note={question.notes}
                  isBookmarked={true}
                  onBookmarkOpen={() => (ignoreNextClickRef.current = true)}
                  packId={notebook.packId}
                  testId={question.testId}
                  notebookData={notebook}
                />
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-[var(--q3-neutral-light)]">
                {activeFilter === 'All Qs' && questions.length === 0
                  ? 'No questions found'
                  : `No ${activeFilter.toLowerCase()} questions found`}
              </p>
              {activeFilter !== 'All Qs' && (
                <button
                  onClick={() => setActiveFilter('All Qs')}
                  className="mt-2 px-4 py-2 text-sm bg-[var(--q3-accent-normal)] text-white rounded-lg"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Pagination - unchanged */}
      {questions.length > 0 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              />
            </PaginationItem>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <PaginationItem key={page}>
                <PaginationLink isActive={page === currentPage} onClick={() => setCurrentPage(page)}>
                  {page}
                </PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationNext
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <div className="flex justify-center mt-8">
        <SimplifyModeToggle />
      </div>

      {isQuestionDrawerOpen && selectedQuestionIndex !== null && (
        <NotebookQuestionDrawer
          isOpen={isQuestionDrawerOpen}
          onClose={() => setIsQuestionDrawerOpen(false)}
          questions={questions}
          currentQuestionIndex={selectedQuestionIndex}
          onQuestionChange={handleQuestionChange}
          testId={notebook.testId}
          packId={notebook.packId}
          autoStartTimer={autoStartTimer}
          onAutoStartTimerChange={setAutoStartTimer}
        />
      )}
    </div>
  );
};
